import React from "react"
import { Spacer } from "gatsby-interface"
import { FadeFromBottom } from "@modules/animation"
import { isBuildRunnerType } from "@modules/build/shared/utils"
import {
  StandardSingleColumn,
  StandardPageContentSection,
} from "@modules/ui/layouts/Containers"
import CopyLink from "./CopyLink"
import { useBuildByIdQuery } from "@modules/build/shared/queries.generated"
import Loading from "@modules/ui/components/Loading"
import useBuildChangedSubscription from "@modules/build/shared/hooks/useBuildChangedSubscription"

import { CancelBuildButton } from "./CancelBuildButton"
import {
  BuildRawLogsSectionHeader,
  BuildRawLogsList,
} from "@modules/build/logs/raw/components/BuildRawLogsList"
import { BuildDetailsInfo } from "./BuildDetailsInfo"
import {
  ErrorAlert,
  useTriggerErrorAlert,
} from "@modules/ui/components/ErrorAlert"
import { BuildsBreadcrumbs } from "./BuildsBreadcrumbs"

const rowCss = theme => ({
  display: `flex`,
  flexDirection: `row`,
  justifyContent: `space-between`,
  margin: `${theme.space[5]} 0 ${theme.space[7]} 0`,
})

const actionsCss = theme => ({
  display: "flex",
  alignItems: "center",
  justifyContent: `space-between`,

  [theme.mediaQueries.desktop]: {
    justifyContent: `unset`,
  },
})

function BuildDetailsInner({ organizationId, site, buildId, loading, error }) {
  const [setCancelBuildError, cancelBuildError] = useTriggerErrorAlert()
  const {
    data,
    error: buildError,
    loading: buildLoading,
  } = useBuildByIdQuery({
    variables: {
      buildId,
    },
    fetchPolicy: `cache-and-network`,
  })

  const buildData = data?.buildById || {}
  const { runnerType, structuredBuild, buildStatus } = buildData

  useBuildChangedSubscription(buildId, runnerType)

  const siteId = site && site.id

  if (error || buildError) {
    const errorToShow = error || buildError
    return (
      <StandardPageContentSection>
        <StandardSingleColumn>
          <BuildsBreadcrumbs
            organizationId={organizationId}
            siteId={siteId}
            build={buildData}
          />
          <Spacer size={5} />
          <ErrorAlert>{errorToShow.message}</ErrorAlert>
        </StandardSingleColumn>
      </StandardPageContentSection>
    )
  }

  if (loading || buildLoading) {
    return <Loading delay={1000} message="Loading build details..." />
  }

  const createdAtDate = new Date(buildData.createdAt)
  const repositoryUrl = site?.repository?.url

  return (
    <StandardPageContentSection>
      <StandardSingleColumn>
        <div css={rowCss}>
          <BuildsBreadcrumbs
            organizationId={organizationId}
            siteId={siteId}
            build={buildData}
          />

          <div css={actionsCss}>
            <CopyLink content={window.location.href} />

            <CancelBuildButton
              siteId={siteId}
              buildId={buildId}
              runnerType={runnerType}
              status={buildStatus}
              error={cancelBuildError}
              setError={setCancelBuildError}
            />
          </div>
        </div>

        {cancelBuildError}

        <FadeFromBottom>
          <React.Fragment>
            <BuildDetailsInfo
              {...buildData}
              buildId={buildId}
              siteId={siteId}
              organizationId={organizationId}
              createdAtDate={createdAtDate}
              repositoryUrl={repositoryUrl}
              previewUrl={site && site.previewUrl}
              incrementalPreviewsEnabled={
                site && site.incrementalPreviewsEnabled
              }
              buildUrl={buildData.url}
            />

            {!structuredBuild && (
              <section
                css={{
                  display: `flex`,
                  flexDirection: `column`,
                  height: `100vh`,
                }}
              >
                <BuildRawLogsSectionHeader>
                  {isBuildRunnerType(runnerType)
                    ? `Build Logs`
                    : `Preview Logs`}
                </BuildRawLogsSectionHeader>
                <BuildRawLogsList buildId={buildId} siteId={siteId} />
              </section>
            )}
          </React.Fragment>
        </FadeFromBottom>
      </StandardSingleColumn>
    </StandardPageContentSection>
  )
}

export default BuildDetailsInner
